<template>
  <v-navigation-drawer
    expand-on-hover
    rail
    style="background-color: rgba(85, 166, 153, 255)"
  >
    <v-list density="compact" nav>
      <v-list-item
        v-for="maps in availableMaps"
        :key="maps.id"
        :value="maps.value"
        :title="maps.title"
        :prepend-icon="maps.icon"
        style="color: white"
        @click="emitSelectedMap(maps.value)"
      ></v-list-item>
    </v-list>
    <v-divider class="my-2"></v-divider>

    <!-- Kartenoptionen. Bewusst hier und nicht in der Legende: die Legende
         erklaert die Karte, sie bedient sie nicht. -->
    <v-list density="compact" nav class="map-options">
      <v-list-item
        prepend-icon="mdi-vector-polygon"
        title="PLZ Gebiete anzeigen"
        style="color: white"
        @click="emitZipcodeToggle(!zipcodeVisible)"
      >
        <template #append>
          <v-checkbox-btn
            class="option-checkbox"
            :model-value="zipcodeVisible"
            density="compact"
            hide-details
            @update:model-value="emitZipcodeToggle(Boolean($event))"
            @click.stop
          ></v-checkbox-btn>
        </template>
      </v-list-item>

      <v-list-item
        v-if="hasSurcharges"
        prepend-icon="mdi-car-clock"
        title="Stauaufschläge berücksichtigen"
        :subtitle="surchargeEnabled ? 'Mobscore bleibt ohne Aufschlag' : ''"
        style="color: white"
        @click="setSurchargeEnabled(!surchargeEnabled)"
      >
        <template #append>
          <v-checkbox-btn
            class="option-checkbox"
            :model-value="surchargeEnabled"
            density="compact"
            hide-details
            @update:model-value="setSurchargeEnabled(Boolean($event))"
            @click.stop
          ></v-checkbox-btn>
        </template>
      </v-list-item>

      <v-list-item
        prepend-icon="mdi-pencil"
        title="Stauaufschläge bearbeiten"
        style="color: white"
        @click="$emit('edit-surcharges')"
      ></v-list-item>
    </v-list>

    <div class="geopackage-container px-2 mb-2 mt-auto">
      <GeoPackageUploader :mapUtilsObj="mapUtilsObj" />
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import GeoPackageUploader from "./GeoPackageUploader.vue";
import { usePlzSurchargeStore } from "../store/plzSurchargeStore";
import { useCleverRouteImportStore } from "../store/cleverRouteImportStore";

export default defineComponent({
  components: {
    GeoPackageUploader,
  },
  props: {
    mapUtilsObj: { type: Object, default: null },
    /**
     * Ob die PLZ-Gebiete gerade auf der Karte liegen.
     *
     * Bewusst eine Prop und kein eigener Zustand: die Karte kann den Layer
     * auch selbst schalten (Bearbeitungsmodus), und ein zweiter Zustand hier
     * wuerde in genau diesen Faellen vom tatsaechlich sichtbaren abweichen.
     */
    zipcodeVisible: { type: Boolean, default: false },
  },
  data() {
    return {
      availableMaps: [
        {
          id: 1,
          value: "distance",
          title: "Entfernungs-Karte",
          icon: "mdi-map-marker-multiple",
        },
        {
          id: 2,
          value: "absolute-time",
          title: "Absolute Fahrtzeiten Karte",
          icon: "mdi-timer-marker-outline",
        },
        {
          id: 3,
          value: "relative-difference",
          title: "Relative Vergleichskarte",
          icon: "mdi-percent",
        },
        {
          id: 4,
          value: "absolute-difference",
          title: "Absolute Vergleichskarte",
          icon: "mdi-plus-minus-variant",
        },
        {
          id: 5,
          value: "best-transport",
          title: "Best-Wert-Karte",
          icon: "mdi-podium-gold",
        },
        {
          id: 6,
          value: "best-alternative",
          title: "Beste Alternative",
          icon: "mdi-podium-silver",
        },
        {
          id: 7,
          value: "multiple-alternatives",
          title: "Anzahl Alternativen",
          icon: "mdi-counter",
        },
      ],
    };
  },
  emits: {
    "toggle-zipcode": (visible: boolean) => {
      return typeof visible === "boolean";
    },
    "surcharge-toggled": () => {
      return true;
    },
    "edit-surcharges": () => {
      return true;
    },
    "show-distance-map": () => {
      return true;
    },
    "show-absolute-time-map": () => {
      return true;
    },
    "show-relative-difference-map": () => {
      return true;
    },
    "show-absolute-difference-map": () => {
      return true;
    },
    "show-best-transport-map": () => {
      return true;
    },
    "show-best-alternative-map": () => {
      return true;
    },
    "show-multiple-alternatives-map": () => {
      return true;
    },
  },
  computed: {
    /**
     * Der Schalter erscheint nur, wenn fuer diesen Standort ueberhaupt
     * Aufschlaege gepflegt sind - sonst schaltet er nichts.
     */
    hasSurcharges(): boolean {
      return usePlzSurchargeStore().hasSurcharges;
    },
    surchargeEnabled(): boolean {
      return usePlzSurchargeStore().enabled;
    },
  },
  methods: {
    emitZipcodeToggle(visible: boolean) {
      this.$emit("toggle-zipcode", visible);
    },
    /**
     * Aufschlag ein- oder ausschalten.
     *
     * Der Store wird direkt geschrieben, damit Karte und Ergebnistabelle
     * denselben Stand sehen; das Ereignis dient nur dem Neuzeichnen der
     * aktiven Karte.
     */
    setSurchargeEnabled(enabled: boolean) {
      useCleverRouteImportStore().setSurchargeEnabled(enabled);
      this.$emit("surcharge-toggled");
    },
    // DEV-NOTE: Use pinia-store for state-management!
    emitSelectedMap(map: string) {
      switch (map) {
        case "distance":
          this.$emit("show-distance-map");
          break;
        case "absolute-time":
          this.$emit("show-absolute-time-map");
          break;
        case "relative-difference":
          this.$emit("show-relative-difference-map");
          break;
        case "absolute-difference":
          this.$emit("show-absolute-difference-map");
          break;
        case "best-transport":
          this.$emit("show-best-transport-map");
          break;
        case "best-alternative":
          this.$emit("show-best-alternative-map");
          break;
        case "multiple-alternatives":
          this.$emit("show-multiple-alternatives-map");
          break;
      }
    },
  },
});
</script>

<style scoped>
/* Kontrollkaestchen sind im eingeklappten Rail-Modus nicht sinnvoll bedienbar
   und wuerden ueber den Rand ragen - dort zaehlt nur das Icon. */
.v-navigation-drawer--rail:not(:hover) .option-checkbox {
  display: none;
}

.map-options :deep(.v-list-item-subtitle) {
  color: rgba(255, 255, 255, 0.85);
  font-style: italic;
}

/* Hide the button when drawer is collapsed */
.v-navigation-drawer--rail .geopackage-container {
  opacity: 0;
  visibility: hidden;
  transition:
    opacity 0.2s,
    visibility 0.2s;
}

/* Show the button when drawer is expanded */
.v-navigation-drawer--expand-on-hover:not(.v-navigation-drawer--rail)
  .geopackage-container,
.v-navigation-drawer--rail:hover .geopackage-container {
  opacity: 1;
  visibility: visible;
}
</style>
